import { Expediente } from "@/models/Expediente.model";
import { TipoDocumento } from "@/models/TipoDocumento.model";
import { DefaultContainer } from "@base/components/layout/containers/DefaultContainer";
import useHttp from "@base/hooks/useHttp/useHttp";
import { PaginaProvider } from "@base/hooks/usePagina/usePagina";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Card, Col, Divider, Progress, Row } from "antd";
import CardDocumento from "./-components/CardDocumento";
import AcuseButton from "@/components/AcuseButton";
import { useAuth } from "@/hooks/useAuth/useAuth";

export const Route = createFileRoute("/(auth)/expediente/")({
  component: () => (
    <PaginaProvider titulo={"Expediente Digital"}>
      <RouteComponent />
    </PaginaProvider>
  ),
});

function RouteComponent() {
  const http = useHttp();
  const { usuario } = useAuth();

  const qDocumentos = useQuery({
    queryKey: [TipoDocumento.ENDPOINTS.DEFAULT, http],
    queryFn: () =>
      http.get({
        endpoint: TipoDocumento.ENDPOINTS.DEFAULT,
        params: { tipo: "" },
      }),
  });

  const qExpedientesAll = useQuery({
    queryKey: [Expediente.ENDPOINTS.DEFAULT, "all", http],
    queryFn: () =>
      http.get({
        endpoint: Expediente.ENDPOINTS.DEFAULT,
        params: { expand: "media" },
      }),
  });

  const documentos: TipoDocumento[] = qDocumentos.data?.resultado ?? [];
  const expedientesAll: Expediente[] = qExpedientesAll.data?.resultado ?? [];

  // Calcular avance
  const totalTipos = documentos.length;
  const atendidosCount = documentos.filter((doc) =>
    expedientesAll.some((exp) => String(exp.idTipo) === String(doc.id)),
  ).length;

  const porcentajeAvance =
    totalTipos > 0 ? Math.round((atendidosCount / totalTipos) * 100) : 0;

  return (
    <DefaultContainer>
      <Row gutter={[24, 16]} align="middle">
        <Col span={24} md={8}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center text-white shadow-lg">
              <Icon icon="lucide:folder-heart" fontSize={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 m-0">
                Expediente Digital
              </h1>
            </div>
          </div>
        </Col>

        <Col span={24} md={8}>
          <Card
            className="shadow-xs border border-gray-200"
            classNames={{ body: "p-3" }}
          >
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center text-sm font-semibold">
                <span className="text-gray-700">Avance de Expediente</span>
                <span className="text-gray-900 font-bold">
                  {porcentajeAvance}% ({atendidosCount}/{totalTipos})
                </span>
              </div>
              <Progress
                percent={porcentajeAvance}
                showInfo={false}
                strokeColor={{ "0%": "#10B981", "100%": "#059669" }}
                status="active"
              />
            </div>
          </Card>
        </Col>

        <Col span={24} md={8}>
          <Row gutter={[10, 10]} justify={"end"}>
            <Col>
              <AcuseButton id={usuario?.id} validado={!!usuario?.validado} />
            </Col>
            <Divider style={{ margin: 0 }} />
            <Col>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon
                  icon={"lucide:folder"}
                  className="text-5xl"
                  style={{ color: "#4ade80" }}
                />
                <span
                  style={{ fontSize: 20, color: "#4ade80", fontWeight: "bold" }}
                >
                  Atendidos
                </span>
              </div>
            </Col>
            <Col>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon
                  icon={"lucide:folder"}
                  className="text-5xl"
                  style={{ color: "#f87171" }}
                />
                <span
                  style={{ fontSize: 20, color: "#f87171", fontWeight: "bold" }}
                >
                  Sin Atender
                </span>
              </div>
            </Col>
          </Row>
        </Col>

        <Divider style={{ margin: 0 }} />
        <Col span={24}>
          <Card classNames={{ body: "p-4" }}>
            <Row gutter={[10, 10]}>
              {documentos.map((doc: TipoDocumento, i: number) => (
                <Col span={12} md={6} key={i}>
                  <CardDocumento
                    documento={doc}
                    onUpdate={() => qExpedientesAll.refetch()}
                  />
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>
    </DefaultContainer>
  );
}
